import BreakpointEnum from '@models/enums/BreakpointEnum';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import getZoomLevel from '@utils/helpers/getZoomLevel';
import { useCallback, useEffect, useState } from 'react';

/* With the viewport breakpoint type, the width is given by the innerWidth prop on window.
This value can change when the user zooms in/out on the page, even if the screen size remains the same.
(e.g. user can be on a desktop screen size but zoom in until they get to a mobile viewport)
With the screen breakpoint type, we first compute the zoom level and then we compute the "screen" width, i.e.
that width that is not affected by the zoom, which should be equal to the innerWidth on the default 100% zoom. */
const getCurrentBreakpoint = (breakpointType: BreakpointTypeEnum) => {
  const breakpoints = { tablet: 768, desktop: 1024, lgDesktop: 1440 };
  const zoomLevel = getZoomLevel();

  const width =
    breakpointType === BreakpointTypeEnum.Viewport
      ? window.innerWidth
      : window.innerWidth * zoomLevel;

  if (width < breakpoints.tablet) {
    return BreakpointEnum.Small;
  }
  if (width >= breakpoints.tablet && width < breakpoints.desktop) {
    return BreakpointEnum.Medium;
  }
  if (width >= breakpoints.desktop && width < breakpoints.lgDesktop) {
    return BreakpointEnum.Large;
  }
  return BreakpointEnum.XLarge;
};

const useBreakpoint = (
  breakpointType: BreakpointTypeEnum = BreakpointTypeEnum.Viewport
) => {
  const [screen, setScreen] = useState<BreakpointEnum>();

  const resizeHandler = useCallback(
    () => setScreen(getCurrentBreakpoint(breakpointType)),
    [breakpointType]
  );

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);
    setScreen(getCurrentBreakpoint(breakpointType));

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [resizeHandler, breakpointType]);

  return screen;
};

export default useBreakpoint;
